import Modal from "./Modal";
import * as XLSX from "xlsx";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
  Sort,
  ColumnMenu,
  ExcelExport,
  PdfExport,
  Toolbar,
} from "@syncfusion/ej2-react-grids";
import React, { useState, useEffect, useRef } from "react";
import { TextBoxComponent } from "@syncfusion/ej2-react-inputs";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../components/AuthContext";
import { decrypt } from "../service/encryption/Encryption";
import LockImage from "../assets/lockbox.png";

const HouseholdsAffectedModal = ({ isOpen, onClose, affectedHouseholds }) => {
  const [showPin, setShowPin] = useState(false);
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [isPinVerified, setIsPinVerified] = useState(false);
  const { role } = useAuth();
  const selectedUserId = sessionStorage.getItem("userId");
  const [inputValues, setInputValues] = useState(["", "", "", ""]);
  const [wrongPinMessage, setWrongPinMessage] = useState(false);
  const inputRefs = useRef([]);

  const clearPin = () => {
    setPin("");
    setInputValues(["", "", "", ""]);
    setWrongPinMessage(false);
    // Don't clear error here, let it be managed separately
  };

  // Update the useEffect for modal open/close
  useEffect(() => {
    if (isOpen) {
      setIsPinVerified(false);
      clearPin();
    }
  }, [isOpen]);

  // Add useEffect to clear error after 2 seconds
  useEffect(() => {
    let timeoutId;
    if (error) {
      timeoutId = setTimeout(() => {
        setError("");
      }, 2000);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [error]);

  const verifyPin = async () => {
    try {
      if (!selectedUserId) {
        setError("User ID not found");
        return;
      }

      const userDoc = await getDoc(doc(db, "users", selectedUserId));
      if (!userDoc.exists()) {
        setError("User not found");
        return;
      }

      let pinDocRef;
      if (role === "partner") {
        pinDocRef = doc(db, "partners", userDoc.data().partner_id);
      } else if (role === "center") {
        pinDocRef = doc(db, "centers", userDoc.data().center_id);
      }

      const pinDoc = await getDoc(pinDocRef);
      if (!pinDoc.exists()) {
        setError("PIN not found");
        return;
      }

      const decryptedPin = decrypt(pinDoc.data().pin);
      if (pin === decryptedPin) {
        setIsPinVerified(true);
        setError("");
      } else {
        setError("Incorrect PIN");
        setPin("");
        setInputValues(["", "", "", ""]);
        inputRefs.current[0].focus();

        setTimeout(() => {
          setError("");
        }, 3000);
      }
    } catch (error) {
      console.error("Error verifying PIN:", error);
      setError("Error verifying PIN");
    }
  };

  const handleExport = () => {
    const dataToExport = affectedHouseholds.map((household) => ({
      "First Name": household.first_name,
      "Last Name": household.last_name,
      Email: household.email,
      "Mobile Number": household.mobile_number,
      Address: household.address,
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(dataToExport, {
      header: Object.keys(dataToExport[0]),
      cellStyles: true,
    });

    // Style the header row
    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const address = XLSX.utils.encode_col(C) + "1";
      if (!ws[address]) continue;
      ws[address].s = {
        font: { bold: true, sz: 12 },
        alignment: { horizontal: "center", vertical: "center" },
      };
    }

    // Calculate and set column widths
    const maxWidths = {};
    const cols = Object.keys(dataToExport[0]);

    // Initialize with header lengths
    cols.forEach((col) => {
      maxWidths[col] = col.length;
    });

    // Check content lengths
    dataToExport.forEach((row) => {
      cols.forEach((col) => {
        const value = row[col] ? String(row[col]) : "";
        maxWidths[col] = Math.max(maxWidths[col], value.length);
      });
    });

    // Set column widths with some padding
    ws["!cols"] = cols.map((col) => ({
      wch: maxWidths[col] + 2, // Add 2 for some padding
    }));

    XLSX.utils.book_append_sheet(wb, ws, "Households");

    const currentDate = new Date().toISOString().split("T")[0];
    const fileName = `HouseholdsAffected_${currentDate}.xlsx`;

    XLSX.writeFile(wb, fileName);
  };

  const onInput = (index, event) => {
    const value = event.target.value;
    if (/^\d$/.test(value)) {
      const newValues = [...inputValues];
      newValues[index] = value;
      setInputValues(newValues);
      setPin(newValues.join(""));

      if (index < inputRefs.current.length - 1) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const onKeyDown = (index, event) => {
    if (event.key === "Backspace") {
      event.preventDefault();

      const newValues = [...inputValues];

      if (inputValues[index]) {
        newValues[index] = "";
      } else if (index > 0) {
        newValues[index - 1] = "";
        inputRefs.current[index - 1].focus();
      }

      setInputValues(newValues);
      setPin(newValues.join(""));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Households Affected Details"
      width="80%"
      height="80%"
      hideLeftButton={true}
      noFooter={true}
    >
      {!isPinVerified ? (
        <div className="flex h-full flex-col items-center justify-center p-4">
          <div className="flex flex-col items-center justify-center rounded-lg p-8">
            <img
              src={LockImage}
              alt="Lockbox"
              className="mb-6 h-[290px] w-[290px]"
            />
            <div className="mb-4 text-center text-sm font-medium text-red-600">
              This section contains personal data, please enter your PIN to
              proceed
            </div>
            <div className="mb-4 flex space-x-8">
              {inputValues.map((value, index) => (
                <input
                  key={index}
                  type="password"
                  maxLength="1"
                  className="lockbox-input h-12 w-12 rounded-xl border border-gray-300 bg-gray-300 text-center text-2xl"
                  pattern="[0-9]"
                  value={value}
                  onChange={(event) => onInput(index, event)}
                  onKeyDown={(event) => onKeyDown(index, event)}
                  ref={(el) => (inputRefs.current[index] = el)}
                />
              ))}
            </div>
            {error && <div className="mb-4 text-red-500">{error}</div>}
            <div className="flex justify-end gap-4">
              <button
                onClick={onClose}
                className="btn-cancel flex cursor-pointer items-center justify-center px-4 py-2"
              >
                <div className="flex items-center justify-center">
                  <svg
                    width="18"
                    height="21"
                    viewBox="0 0 18 21"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="mr-2"
                  >
                    <g id="Shield Cancel">
                      <path
                        id="Fill 1"
                        opacity="0.4"
                        d="M9.08649 20.5C8.96266 20.5 8.83884 20.4716 8.72709 20.4137L5.12599 18.5496C4.10415 18.0201 3.30481 17.4259 2.68063 16.7336C1.31449 15.2195 0.554402 13.276 0.542321 11.2599L0.500038 4.62426C0.495004 3.85842 0.989312 3.17103 1.72826 2.91215L8.3405 0.606788C8.73313 0.466564 9.17106 0.464603 9.57073 0.599924L16.2081 2.82684C16.9511 3.07493 17.4535 3.75742 17.4575 4.52228L17.4998 11.1628C17.5129 13.176 16.779 15.1274 15.434 16.6581C14.8168 17.3602 14.0245 17.9632 13.0128 18.5025L9.44388 20.4088C9.33314 20.4686 9.21032 20.499 9.08649 20.5Z"
                        fill="#FF0000"
                      />
                      <path
                        id="Fill 4"
                        d="M12.7071 13.7071C13.0976 13.3166 13.0976 12.6834 12.7071 12.2929L10.4142 10L12.7071 7.70711C13.0976 7.31658 13.0976 6.68342 12.7071 6.29289C12.3166 5.90237 11.6834 5.90237 11.2929 6.29289L9 8.58579L6.70711 6.29289C6.31658 5.90237 5.68342 5.90237 5.29289 6.29289C4.90237 6.68342 4.90237 7.31658 5.29289 7.70711L7.58579 10L5.29289 12.2929C4.90237 12.6834 4.90237 13.3166 5.29289 13.7071C5.68342 14.0976 6.31658 14.0976 6.70711 13.7071L9 11.4142L11.2929 13.7071C11.6834 14.0976 12.3166 14.0976 12.7071 13.7071Z"
                        fill="#FF0000"
                      />
                    </g>
                  </svg>
                  Close
                </div>
              </button>
              <button
                onClick={verifyPin}
                className="btn-default flex cursor-pointer items-center justify-center px-4 py-2"
              >
                <div className="flex items-center justify-center">
                  <svg
                    width="18"
                    height="21"
                    viewBox="0 0 18 21"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="mr-2"
                  >
                    <g id="Shield Done">
                      <path
                        id="Fill 1"
                        opacity="0.4"
                        d="M9.08649 20.5C8.96266 20.5 8.83884 20.4716 8.72709 20.4137L5.12599 18.5496C4.10415 18.0201 3.30481 17.4259 2.68063 16.7336C1.31449 15.2195 0.554402 13.276 0.542321 11.2599L0.500038 4.62426C0.495004 3.85842 0.989312 3.17103 1.72826 2.91215L8.3405 0.606788C8.73313 0.466564 9.17106 0.464603 9.57073 0.599924L16.2081 2.82684C16.9511 3.07493 17.4535 3.75742 17.4575 4.52228L17.4998 11.1628C17.5129 13.176 16.779 15.1274 15.434 16.6581C14.8168 17.3602 14.0245 17.9632 13.0128 18.5025L9.44388 20.4088C9.33314 20.4686 9.21032 20.499 9.08649 20.5Z"
                        fill="white"
                      />
                      <path
                        id="Fill 4"
                        d="M8.31936 12.8209C8.12607 12.8219 7.93278 12.7523 7.78378 12.6091L5.86695 10.7656C5.57097 10.4793 5.56795 10.0145 5.86091 9.72617C6.15387 9.4369 6.63207 9.43396 6.92906 9.71931L8.30829 11.0451L11.6758 7.72479C11.9698 7.43552 12.448 7.43258 12.744 7.71793C13.041 8.00426 13.044 8.47004 12.751 8.75735L8.85193 12.6022C8.70494 12.7474 8.51266 12.8199 8.31936 12.8209Z"
                        fill="white"
                      />
                    </g>
                  </svg>
                  Confirm
                </div>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex h-full flex-col">
          <div className="min-h-0 flex-1">
            <GridComponent
              dataSource={affectedHouseholds}
              allowPaging={true}
              allowSorting={true}
              // filterSettings={{ type: "CheckBox" }}
              // allowFiltering={true}
              sortSettings={{ allowSorting: true }}
              pageSettings={{ pageSize: 10 }}
              showColumnMenu={true}
              // columnMenuItems={["SortAscending", "SortDescending", "Filter"]}
              allowExcelExport={true}
              allowPdfExport={true}
              cssClass="new-grid-color h-full"
              height="100%"
            >
              <ColumnsDirective>
                <ColumnDirective
                  field="first_name"
                  headerText="First Name"
                  width="120"
                />
                <ColumnDirective
                  field="last_name"
                  headerText="Last Name"
                  width="120"
                />
                <ColumnDirective field="email" headerText="Email" width="200" />
                <ColumnDirective
                  field="mobile_number"
                  headerText="Mobile Number"
                  width="120"
                />
                <ColumnDirective
                  field="address"
                  headerText="Address"
                  width="200"
                />
              </ColumnsDirective>
              <Inject
                services={[
                  Page,
                  Sort,
                  ColumnMenu,
                  ExcelExport,
                  PdfExport,
                  Toolbar,
                ]}
              />
            </GridComponent>
          </div>

          <div className="mt-auto border-t px-5 py-4">
            <div className="flex justify-between gap-4">
              <button
                onClick={onClose}
                className="hover:bg-gray-50 w-full cursor-pointer rounded-[4px] border border-[#0a2558] bg-white px-4 py-4 font-['Inter'] text-2xl font-medium text-[#003460]"
              >
                Close
              </button>
              <button
                onClick={handleExport}
                className="w-full cursor-pointer rounded-[4px] bg-[#003460] px-4 py-4 font-['Inter'] text-2xl font-medium text-[#F7CA41] hover:bg-[#003460]/90"
              >
                Export
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default HouseholdsAffectedModal;
